import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  Typography,
  IconButton,
  Button,
} from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import MttTextField from 'components/Material/MttTextField/MttTextFIeld'
import { useFintalkCloud } from 'contexts/FintalkCloud/fintalkCloudContext'
import { IJsonFlowEditorData, IPromptVersion } from 'models/JsonFlowEditor'
import {
  ArrowBack,
  ArrowForward,
  Save,
  GetApp,
  Publish,
  ExitToApp,
} from '@material-ui/icons'
import { Wand } from 'components/Icons'
import { callLlmGateway } from 'services/LlmGatewayService'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    textFieldContainer: {
      position: 'relative',
      marginBottom: theme.spacing(1),
    },
    textField: {
      width: '100%',
    },
    magicWandButton: {
      position: 'absolute',
      right: 10,
      top: 10,
    },
    versionInfo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
    },
    versionNavigation: {
      display: 'flex',
      alignItems: 'center',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(2),
      gap: theme.spacing(2),
    },
    jsonPreviewContainer: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      maxHeight: '300px',
      overflow: 'auto',
    },
    jsonPreview: {
      whiteSpace: 'pre-wrap',
      fontFamily: 'monospace',
      fontSize: '14px',
    },
  }),
)

export function JsonFlowEditor() {
  const classes = useStyles()
  const { currentData } = useFintalkCloud()
  const [showJsonPreview, setShowJsonPreview] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [data, setData] = useState<IJsonFlowEditorData>({
    userPrompt: {
      current: '',
      versions: [],
      currentVersionIndex: -1,
    },
    systemPrompt: {
      current: '',
      versions: [],
      currentVersionIndex: -1,
    },
    dataflow: null,
  })

  useEffect(() => {
    const savedData = localStorage.getItem('jsonFlowEditor')
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData)
        setData(parsedData)
      } catch (error) {
        console.error('Erro ao carregar dados salvos:', error)
      }
    }
  }, [])

  const savePrompt = (type: 'userPrompt' | 'systemPrompt') => {
    const currentContent = data[type].current
    const newVersion: IPromptVersion = {
      content: currentContent,
      timestamp: new Date().toISOString(),
      user: currentData.agentName || 'Usuário',
      version:
        data[type].versions.length > 0 ? data[type].versions[0].version + 1 : 1,
    }

    const newData = {
      ...data,
      [type]: {
        ...data[type],
        versions: [newVersion, ...data[type].versions],
        currentVersionIndex: 0,
      },
    }

    setData(newData)
    localStorage.setItem('jsonFlowEditor', JSON.stringify(newData))
  }

  const navigateVersion = (
    type: 'userPrompt' | 'systemPrompt',
    direction: 'prev' | 'next',
  ) => {
    const currentIndex = data[type].currentVersionIndex
    const versions = data[type].versions

    if (versions.length === 0) return

    let newIndex
    if (direction === 'prev') {
      newIndex = Math.min(currentIndex + 1, versions.length - 1)
    } else {
      newIndex = Math.max(currentIndex - 1, 0)
    }

    const newData = {
      ...data,
      [type]: {
        ...data[type],
        current: versions[newIndex].content,
        currentVersionIndex: newIndex,
      },
    }

    setData(newData)
  }

  const handlePromptChange = (
    type: 'userPrompt' | 'systemPrompt',
    value: string,
  ) => {
    setData({
      ...data,
      [type]: {
        ...data[type],
        current: value,
      },
    })
  }

  const handleExecute = async () => {
    if (!data.dataflow) {
      alert('É necessário importar um JSON de fluxo antes de executar.')
      return
    }

    setProcessing(true)
    try {
      const result = await callLlmGateway(
        data.systemPrompt.current,
        data.userPrompt.current,
      )

      alert('Resposta do modelo:\n\n' + result)
    } catch (error) {
      console.error('Erro ao processar:', error)
      alert(
        'Erro ao processar o pedido. Verifique o console para mais detalhes.',
      )
    } finally {
      setProcessing(false)
    }
  }

  const handleImportJson = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/json'
    input.onchange = async (e: any) => {
      const file = e.target.files?.item(0)
      if (!file) return

      try {
        const jsonText = await file.text()
        const json = JSON.parse(jsonText)
        setData({
          ...data,
          dataflow: json,
        })
        localStorage.setItem(
          'jsonFlowEditor',
          JSON.stringify({
            ...data,
            dataflow: json,
          }),
        )
      } catch (error) {
        console.error('Erro ao importar JSON:', error)
        alert('Erro ao importar JSON. Verifique o formato do arquivo.')
      }
    }
    input.click()
  }

  const handleExportJson = () => {
    if (!data.dataflow) {
      alert('Não há dados para exportar.')
      return
    }

    const jsonString = JSON.stringify(data.dataflow, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dataflow-${new Date().toISOString().replace(/:/g, '-')}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExit = () => {
    localStorage.setItem('jsonFlowEditor', JSON.stringify(data))
    window.history.back()
  }

  const handleMagicWand = async () => {
    alert('Funcionalidade de melhoria não implementada')
  }

  const toggleJsonPreview = () => {
    setShowJsonPreview(!showJsonPreview)
  }

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper}>
        <Typography variant="h5" gutterBottom>
          Editor de Fluxo Conversacional
        </Typography>

        <Typography variant="h6" gutterBottom>
          Pedido (User Prompt)
        </Typography>
        <div className={classes.textFieldContainer}>
          <MttTextField
            multiline
            rows={8}
            value={data.userPrompt.current}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handlePromptChange('userPrompt', e.target.value)
            }
            variant="outlined"
            placeholder="Digite seu pedido de modificação do fluxo..."
            fullWidth
          />
          <IconButton
            className={classes.magicWandButton}
            onClick={handleMagicWand}
            title="Melhorar script"
          >
            <Wand />
          </IconButton>
        </div>

        <div className={classes.versionInfo}>
          <div>
            {data.userPrompt.versions.length > 0 &&
              data.userPrompt.currentVersionIndex >= 0 && (
                <Typography variant="body2">
                  Última versão:{' '}
                  {new Date(
                    data.userPrompt.versions[
                      data.userPrompt.currentVersionIndex
                    ].timestamp,
                  ).toLocaleString()}{' '}
                  - Usuário:{' '}
                  {
                    data.userPrompt.versions[
                      data.userPrompt.currentVersionIndex
                    ].user
                  }
                </Typography>
              )}
          </div>
          <div className={classes.versionNavigation}>
            <IconButton
              disabled={
                data.userPrompt.versions.length === 0 ||
                data.userPrompt.currentVersionIndex >=
                  data.userPrompt.versions.length - 1
              }
              onClick={() => navigateVersion('userPrompt', 'prev')}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              disabled={
                data.userPrompt.versions.length === 0 ||
                data.userPrompt.currentVersionIndex <= 0
              }
              onClick={() => navigateVersion('userPrompt', 'next')}
            >
              <ArrowForward />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={() => savePrompt('userPrompt')}
            >
              Salvar
            </Button>
          </div>
        </div>

        <Typography variant="h6" gutterBottom style={{ marginTop: '24px' }}>
          System Prompt
        </Typography>
        <div className={classes.textFieldContainer}>
          <MttTextField
            multiline
            rows={8}
            value={data.systemPrompt.current}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handlePromptChange('systemPrompt', e.target.value)
            }
            variant="outlined"
            placeholder="Digite o system prompt..."
            fullWidth
          />
        </div>

        <div className={classes.versionInfo}>
          <div>
            {data.systemPrompt.versions.length > 0 &&
              data.systemPrompt.currentVersionIndex >= 0 && (
                <Typography variant="body2">
                  Última versão:{' '}
                  {new Date(
                    data.systemPrompt.versions[
                      data.systemPrompt.currentVersionIndex
                    ].timestamp,
                  ).toLocaleString()}{' '}
                  - Usuário:{' '}
                  {
                    data.systemPrompt.versions[
                      data.systemPrompt.currentVersionIndex
                    ].user
                  }
                </Typography>
              )}
          </div>
          <div className={classes.versionNavigation}>
            <IconButton
              disabled={
                data.systemPrompt.versions.length === 0 ||
                data.systemPrompt.currentVersionIndex >=
                  data.systemPrompt.versions.length - 1
              }
              onClick={() => navigateVersion('systemPrompt', 'prev')}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              disabled={
                data.systemPrompt.versions.length === 0 ||
                data.systemPrompt.currentVersionIndex <= 0
              }
              onClick={() => navigateVersion('systemPrompt', 'next')}
            >
              <ArrowForward />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={() => savePrompt('systemPrompt')}
            >
              Salvar
            </Button>
          </div>
        </div>

        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            disabled={processing || !data.dataflow}
            onClick={handleExecute}
          >
            ▶️ Executar
          </Button>
          <Button variant="contained" onClick={toggleJsonPreview}>
            👁️ {showJsonPreview ? 'Ocultar' : 'Visualizar'} JSON
          </Button>
          <Button
            variant="contained"
            startIcon={<Publish />}
            onClick={handleImportJson}
          >
            📥 Importar
          </Button>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={handleExportJson}
            disabled={!data.dataflow}
          >
            📤 Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<ExitToApp />}
            onClick={handleExit}
          >
            🚪 Sair
          </Button>
        </div>

        {showJsonPreview && data.dataflow && (
          <Paper className={classes.jsonPreviewContainer}>
            <pre className={classes.jsonPreview}>
              {JSON.stringify(data.dataflow, null, 2)}
            </pre>
          </Paper>
        )}
      </Paper>
    </Container>
  )
}
